// src/pages/EarningsPage.jsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Divider,
  Checkbox,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AccountBalanceWallet,
  TrendingUp,
  History,
  Payment,
  Download,
  CheckCircle,
  Pending,
  Schedule,
  Add,
  VerifiedUser,
  Upload,
  Camera,
  CreditCard,
  AccountBalance,
  Delete,
  Edit
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer
} from 'recharts';
import DashboardLayout from '../components/DashboardLayout';
import PaypalIcon from '../icons/PaypalIcon';

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Add Payment Method Dialog Component
const AddPaymentMethodDialog = ({
  open,
  onClose,
  onAdd,
  loading,
  method = {},
  onChange,
  isValidPaymentMethod,
  paymentMethods = [],
  billingInfo,
  onBillingInfoChange,
}) => {
  const safeMethod = {
    type: method?.type || 'credit_card',
    cardNumber: method?.cardNumber || '',
    expiryMonth: method?.expiryMonth || '',
    expiryYear: method?.expiryYear || '',
    cvv: method?.cvv || '',
    cardholderName: method?.cardholderName || '',
    paypalEmail: method?.paypalEmail || '',
    cashappCashtag: method?.cashappCashtag || '',
    isDefault: method?.isDefault || false
  };

  // Check if user already has a PayPal account
  const hasExistingPayPal = paymentMethods.some(method => method.type === 'paypal');
  const existingPayPal = paymentMethods.find(method => method.type === 'paypal');
  const existingPayPalEmail = existingPayPal?.paypal_email || existingPayPal?.email;

  const handleBillingInputChange = (field, value) => {
    onBillingInfoChange(field, value);
  };

  const currentYear = new Date().getFullYear();
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = Array.from({ length: 10 }, (_, i) => currentYear + i);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    return parts.length ? parts.join(' ') : value;
  };

  const handleInputChange = (field, value) => {
    onChange({
      ...safeMethod,
      [field]: value
    });
  };

  const handleCardNumberChange = (value) => {
    const formattedValue = formatCardNumber(value.replace(/\D/g, ''));
    handleInputChange('cardNumber', formattedValue.replace(/\s/g, ''));
  };

  // Handle payment method type change
  const handleTypeChange = (newType) => {
    // If user tries to select PayPal but already has one, don't change the type
    if (newType === 'paypal' && hasExistingPayPal) {
      return;
    }
    
    handleInputChange('type', newType);
    if (newType !== 'credit_card') {
      Object.keys(billingInfo).forEach(key => {
        onBillingInfoChange(key, '');
      });
    }
  };

  // Handle add payment method with PayPal validation
  const handleAddWithValidation = () => {
    // Double-check PayPal restriction before submitting
    if (safeMethod.type === 'paypal' && hasExistingPayPal) {
      return;
    }
    onAdd();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Add sx={{ mr: 1 }} />
          Add Payment Method
        </Box>
      </DialogTitle>

      <DialogContent>


        {/* Payment Method Type Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <FormLabel>Payment Method Type</FormLabel>
          <RadioGroup
            value={safeMethod.type}
            onChange={(e) => handleTypeChange(e.target.value)}
            row
          >
            <FormControlLabel
              value="credit_card"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CreditCard sx={{ mr: 1, fontSize: 20 }} />
                  Credit Card
                </Box>
              }
            />
            
            {/* PayPal Option - Completely disabled when already exists */}
            <Tooltip 
              title={hasExistingPayPal ? "You already have a PayPal account. Delete the existing one to add a new one." : ""}
              placement="top"
            >
              <Box>
                <FormControlLabel
                  value="paypal"
                  control={<Radio />}
                  disabled={hasExistingPayPal}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', opacity: hasExistingPayPal ? 0.5 : 1 }}>
                      <PaypalIcon sx={{ mr: 1, fontSize: 20 }} />
                      PayPal
                      {hasExistingPayPal && (
                        <Chip 
                          label="Limit Reached" 
                          size="small" 
                          color="warning" 
                          sx={{ ml: 1, height: 20 }} 
                        />
                      )}
                    </Box>
                  }
                />
              </Box>
            </Tooltip>
            
            <FormControlLabel
              value="cashapp"
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccountBalance sx={{ mr: 1, fontSize: 20 }} />
                  CashApp
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Credit Card Form */}
        {safeMethod.type === 'credit_card' && (
          <>
            <TextField
              fullWidth
              label="Card Number"
              value={formatCardNumber(safeMethod.cardNumber)}
              onChange={(e) => handleCardNumberChange(e.target.value)}
              placeholder="1234 5678 9012 3456"
              inputProps={{ maxLength: 19 }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Cardholder Name"
              value={safeMethod.cardholderName}
              onChange={(e) => handleInputChange('cardholderName', e.target.value)}
              placeholder="John Doe"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Expiry Month</InputLabel>
                <Select
                  value={safeMethod.expiryMonth}
                  label="Expiry Month"
                  onChange={(e) => handleInputChange('expiryMonth', e.target.value)}
                >
                  {months.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month.toString().padStart(2, '0')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Expiry Year</InputLabel>
                <Select
                  value={safeMethod.expiryYear}
                  label="Expiry Year"
                  onChange={(e) => handleInputChange('expiryYear', e.target.value)}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              fullWidth
              label="CVV"
              value={safeMethod.cvv}
              onChange={(e) => handleInputChange('cvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="123"
              type="password"
              sx={{ mb: 2 }}
            />

            {/* Billing Information */}
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Billing Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Billing Name"
                  value={billingInfo.name}
                  onChange={(e) => handleBillingInputChange('name', e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Billing Email"
                  type="email"
                  value={billingInfo.email}
                  onChange={(e) => handleBillingInputChange('email', e.target.value)}
                  placeholder="john.doe@example.com"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Billing Phone"
                  value={billingInfo.phone}
                  onChange={(e) => handleBillingInputChange('phone', e.target.value)}
                  placeholder="+44 123 456 7890"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={billingInfo.address_line1}
                  onChange={(e) => handleBillingInputChange('address_line1', e.target.value)}
                  placeholder="123 Main Street"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={billingInfo.address_line2}
                  onChange={(e) => handleBillingInputChange('address_line2', e.target.value)}
                  placeholder="Apt 4B"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={billingInfo.city}
                  onChange={(e) => handleBillingInputChange('city', e.target.value)}
                  placeholder="London"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State/Province"
                  value={billingInfo.state}
                  onChange={(e) => handleBillingInputChange('state', e.target.value)}
                  placeholder="England"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={billingInfo.postal_code}
                  onChange={(e) => handleBillingInputChange('postal_code', e.target.value)}
                  placeholder="SW1A 1AA"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={billingInfo.country}
                  onChange={(e) => handleBillingInputChange('country', e.target.value)}
                  placeholder="United Kingdom"
                  required
                />
              </Grid>
            </Grid>
          </>
        )}
        
        {/* PayPal Form - Only show if no existing PayPal */}
        {safeMethod.type === 'paypal' && !hasExistingPayPal && (
          <>
            <TextField
              fullWidth
              label="PayPal Email"
              value={safeMethod.paypalEmail}
              onChange={(e) => handleInputChange('paypalEmail', e.target.value)}
              placeholder="your.email@example.com"
              type="email"
              sx={{ mb: 2 }}
            />
            <Alert severity="info">
              <Typography variant="body2">
                <strong>PayPal Information:</strong> Only your PayPal email is required. 
                No billing address needed for PayPal transfers.
              </Typography>
            </Alert>
          </>
        )}

        {/* CashApp Form */}
        {safeMethod.type === 'cashapp' && (
          <>
            <TextField
              fullWidth
              label="CashApp Cashtag"
              value={safeMethod.cashappCashtag}
              onChange={(e) => handleInputChange('cashappCashtag', e.target.value)}
              placeholder="$YourCashtag"
              sx={{ mb: 2 }}
            />
            <Alert severity="info">
              <Typography variant="body2">
                <strong>CashApp Information:</strong> Only your CashApp cashtag is required. 
                No billing address needed for CashApp transfers.
              </Typography>
            </Alert>
          </>
        )}
        
        {/* Default Payment Checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={safeMethod.isDefault}
              onChange={(e) => handleInputChange('isDefault', e.target.checked)}
              disabled={paymentMethods.length > 0}
            />
          }
          label="Set as default payment method"
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAddWithValidation}
          variant="contained"
          disabled={
            loading || 
            !isValidPaymentMethod(safeMethod) ||
            (safeMethod.type === 'paypal' && hasExistingPayPal)
          }
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}
        >
          {loading ? 'Adding...' : 'Add Payment Method'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddPaymentMethodDialog.defaultProps = {
  method: {},
  onChange: () => {},
  onClose: () => {},
  onAdd: () => {},
  loading: false,
  open: false,
  paymentMethods: [],
  isValidPaymentMethod: () => false,
  billingInfo: {},
  onBillingInfoChange: () => {} 
};

// Edit Payment Method Dialog Component
const EditPaymentMethodDialog = ({
  open,
  onClose,
  method,
  onUpdate,
  onDelete,
  loading,
  paymentMethods = [],
  defaultPaymentMethod
}) => {
  const [isDefault, setIsDefault] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false); // Local state for delete confirmation

  useEffect(() => {
    if (method) {
      setIsDefault(method.id === defaultPaymentMethod);
    }
  }, [method, defaultPaymentMethod]);

  const handleSave = () => {
    if (method) {
      onUpdate(method.id, { is_default: isDefault });
    }
  };

  const handleDelete = () => {
    if (method) {
      onDelete(method.id);
      setDeleteConfirmOpen(false); // Close the confirmation dialog
    }
  };

  // Don't render anything if method is null
  if (!method) {
    return null;
  }

  return (
    <Dialog open={open && !!method} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Payment sx={{ mr: 1 }} />
          Edit Payment Method
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Display payment method info with null checks */}
          <Typography variant="h6" gutterBottom>
            {method?.type === 'credit_card' ? 'Credit Card' : 
             method?.type === 'paypal' ? 'PayPal' : 'CashApp'}
          </Typography>
          
          <Typography variant="body1" gutterBottom>
            {method?.type === 'credit_card' ? `•••• ${method?.last4 || ''}` :
             method?.type === 'paypal' ? method?.email || '' : method?.cashtag || ''}
          </Typography>

          {method?.type === 'credit_card' && (
            <Typography variant="body2" color="text.secondary">
              Expires: {method?.exp_month || ''}/{method?.exp_year || ''}
            </Typography>
          )}

          {/* Default payment method toggle */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
              />
            }
            label="Set as default payment method"
            sx={{ mt: 2 }}
          />

          {isDefault && method?.id !== defaultPaymentMethod && (
            <Alert severity="info" sx={{ mt: 1 }}>
              This will replace your current default payment method
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button 
          onClick={() => setDeleteConfirmOpen(true)} 
          color="error"
          startIcon={<Delete />}
          disabled={!method}
        >
          Delete
        </Button>
        
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={loading || !method}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Payment Method</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this payment method? This action cannot be undone.
          </Typography>
          {method?.id === defaultPaymentMethod && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This is your default payment method. You'll need to set a new default after deletion.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={!method}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

EditPaymentMethodDialog.defaultProps = {
  method: null,
  onClose: () => {},
  onUpdate: () => {},
  onDelete: () => {},
  loading: false
};

export default function EarningsPage() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [earningsData, setEarningsData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false);
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    type: 'credit_card',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: '',
    paypalEmail: '',
    cashappCashtag: '',
    isDefault: false
  });
  const [addingPayment, setAddingPayment] = useState(false);

  // Billing info state
  const [billingInfo, setBillingInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });

  // Verification state
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationStep, setVerificationStep] = useState(0);
  const [verificationDocuments, setVerificationDocuments] = useState({
    idFront: null,
    idBack: null,
    selfie: null
  });
  const [uploading, setUploading] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [verificationStatus, setVerificationStatus] = useState({
    status: 'checking',
    data: null
  });

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const handleBillingInfoChange = (field, value) => {
    setBillingInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetBillingInfo = () => {
    setBillingInfo({
      name: '',
      email: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: ''
    });
  };

  const isValidPaymentMethod = useCallback((method = {}) => {
    // Strict validation for PayPal duplicates
    if (method.type === 'paypal') {
      const hasExistingPayPal = paymentMethods.some(pm => pm.type === 'paypal');
      if (hasExistingPayPal) {
        return false;
      }
      // Email validation for PayPal
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(method.paypalEmail || '');
    }

    switch (method.type) {
      case 'credit_card':
        return (
          method.cardNumber?.replace(/\s/g, '').length === 16 &&
          method.cardholderName?.trim().length > 0 &&
          method.expiryMonth &&
          method.expiryYear &&
          method.cvv?.length >= 3 &&
          billingInfo.name?.trim().length > 0 &&
          billingInfo.email?.trim().length > 0 &&
          billingInfo.address_line1?.trim().length > 0 &&
          billingInfo.city?.trim().length > 0 &&
          billingInfo.postal_code?.trim().length > 0 &&
          billingInfo.country?.trim().length > 0
        );
      case 'cashapp':
        return (method.cashappCashtag || '').startsWith('$') && method.cashappCashtag?.length > 1;
      default:
        return false;
    }
  }, [billingInfo, paymentMethods]);

  const getCardBrand = (cardNumber) => {
    const cleanNumber = cardNumber.replace(/\s/g, '');
    if (/^4/.test(cleanNumber)) return 'Visa';
    if (/^5[1-5]/.test(cleanNumber)) return 'Mastercard';
    if (/^3[47]/.test(cleanNumber)) return 'American Express';
    if (/^6(?:011|5)/.test(cleanNumber)) return 'Discover';
    return 'Unknown';
  };

  const updateDefaultPaymentMethod = async (methodId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `https://propertyearn.net/api/api/user/payment-methods/${methodId}/set-default`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to set default payment method');

      setSnackbar({
        open: true,
        message: data.message || 'Default payment method updated successfully!',
        severity: 'success'
      });

      await fetchPaymentMethods();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/user/payment-methods-detailed', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch payment methods');

      const data = await response.json();
      setPaymentMethods(data.payment_methods);
      setDefaultPaymentMethod(data.default_payment_method);
      setSelectedPaymentMethod(data.default_payment_method || '');
    } catch (err) {
      setError(err.message);
      console.error('Error fetching payment methods:', err);
    }
  };

  const fetchEarningsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/earnings/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch earnings data');

      const data = await response.json();
      setEarningsData(data);
    } catch (err) {
      setError(err.message);
    }
  };
  
  const fetchTransactions = async (page, limit) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `https://propertyearn.net/api/api/earnings/transactions?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch transactions');

      const data = await response.json();
      setTransactions(data.transactions);
      setPagination(data.pagination);
    } catch (err) {
      setError(err.message);
    }
  };

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchEarningsData(),
          fetchTransactions(1, rowsPerPage),
          fetchPaymentMethods(),
          checkVerificationStatus()
        ]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/user/verification/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        setVerificationStatus({
          status: data.verification_status || 'not_started',
          data: data
        });
        
        return data.verification_status === 'approved';
      }
      
      setVerificationStatus({
        status: 'not_started',
        data: null
      });
      return false;
      
    } catch (error) {
      console.error('Error checking verification status:', error);
      setVerificationStatus({
        status: 'error',
        data: null
      });
      return false;
    }
  };

  // FIXED: Always require PayPal verification for PayPal withdrawals
  const handleWithdrawal = async () => {
    try {
      // Check ACCOUNT verification status (general identity verification)
      const isAccountVerified = await checkVerificationStatus();
      
      if (!isAccountVerified) {
        setVerificationDialogOpen(true);
        return;
      }

      // Validate payment method selection
      if (!selectedPaymentMethod) {
        setSnackbar({
          open: true,
          message: 'Please select a payment method',
          severity: 'error'
        });
        return;
      }

      // Validate amount
      const amount = parseFloat(withdrawAmount);
      if (amount < 10 || amount > (earningsData?.available_balance || 0)) {
        setSnackbar({
          open: true,
          message: 'Invalid withdrawal amount. Minimum is £10 and cannot exceed available balance.',
          severity: 'error'
        });
        return;
      }

      // Get selected payment method details
      const selectedMethod = paymentMethods.find(method => method.id === selectedPaymentMethod);
      
      // ALWAYS require PayPal verification for PayPal withdrawals
      if (selectedMethod?.type === 'paypal') {
        await handlePayPalWithdrawal(selectedMethod, amount);
        return;
      }

      // Handle other payment methods
      await processStandardWithdrawal(amount);
      
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message,
        severity: 'error'
      });
    }
  };

  // Process standard withdrawal (non-PayPal)
  const processStandardWithdrawal = async (amount) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('https://propertyearn.net/api/api/withdrawals/request', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amount,
          payment_method_id: selectedPaymentMethod
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Withdrawal failed');
      }

      setSnackbar({
        open: true,
        message: data.message || 'Withdrawal processed successfully!',
        severity: 'success'
      });

      await fetchEarningsData();
      setWithdrawDialogOpen(false);
      setWithdrawAmount('');
      
    } catch (error) {
      throw error;
    }
  };

    // PayPal withdrawal flow - ALWAYS requires verification
    // PayPal withdrawal flow - Skip verification if already verified
    const handlePayPalWithdrawal = async (paypalMethod, amount) => {
      try {
        const token = localStorage.getItem('token');
        
        // Step 1: Initiate PayPal verification
        const verificationResponse = await fetch('https://propertyearn.net/api/api/withdrawals/paypal-verification', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            amount: amount,
            paypal_email: paypalMethod.paypal_email || paypalMethod.email
          })
        });

        const verificationData = await verificationResponse.json();
        
        if (!verificationResponse.ok) {
          throw new Error(verificationData.error || 'Failed to initiate PayPal verification');
        }

        // Step 2: Check if verification can be skipped
        if (verificationData.status === 'already_verified' || verificationData.skip_verification) {
          console.log('✅ PayPal already verified, skipping external verification');
          
          // Directly process withdrawal without popup
          await processPayPalWithdrawal(amount, paypalMethod.id, verificationData.paypal_email);
          return;
        }

        // Step 3: If verification required, open popup window
        const popupWidth = 600;
        const popupHeight = 700;
        const left = (window.screen.width - popupWidth) / 2;
        const top = (window.screen.height - popupHeight) / 2;
        
        const popupWindow = window.open(
          verificationData.verification_url,
          'PayPalVerification',
          `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );

        if (!popupWindow) {
          throw new Error('Popup window blocked. Please allow popups for this website.');
        }

        // Step 4: Poll for verification status (only if popup was opened)
        const checkVerificationStatus = async () => {
          try {
            const statusResponse = await fetch('https://propertyearn.net/api/api/withdrawals/paypal-verification-status', {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            if (statusResponse.ok) {
              const statusData = await statusResponse.json();
              
              if (statusData.status === 'verified') {
                // ✅ Verification successful - close popup and process withdrawal
                if (popupWindow && !popupWindow.closed) {
                  popupWindow.close();
                }
                
                // Process the withdrawal immediately
                await processPayPalWithdrawal(amount, paypalMethod.id, statusData.paypal_email);
                
              } else if (statusData.status === 'failed') {
                // ❌ Verification failed - close popup and show error
                if (popupWindow && !popupWindow.closed) {
                  popupWindow.close();
                }
                throw new Error(`PayPal verification failed: ${statusData.reason || 'Unknown reason'}`);
                
              } else {
                // 🔄 Still pending, check again in 2 seconds
                setTimeout(checkVerificationStatus, 2000);
              }
            } else {
              // ❌ API error - close popup and show error
              if (popupWindow && !popupWindow.closed) {
                popupWindow.close();
              }
              throw new Error('Failed to check verification status');
            }
          } catch (error) {
            console.error('Error checking verification status:', error);
            if (popupWindow && !popupWindow.closed) {
              popupWindow.close();
            }
            // ✅ FIXED: Corrected the typo from setSnackbTar to setSnackbar
            setSnackbar({
              open: true,
              message: error.message || 'Verification status check failed',
              severity: 'error'
            });
          }
        };

        // Start polling after a short delay to let the popup load
        setTimeout(checkVerificationStatus, 3000);

        // Also check if user manually closes the popup
        const checkPopupClosed = setInterval(() => {
          if (popupWindow.closed) {
            clearInterval(checkPopupClosed);
            // ✅ FIXED: Corrected the typo from setSnackbTar to setSnackbar
            setSnackbar({
              open: true,
              message: 'Please complete the verification process or click "Check Verification Status" if you have already completed it.',
              severity: 'warning'
            });
          }
        }, 1000);

      } catch (error) {
        // ✅ FIXED: Corrected the typo from setSnackbTar to setSnackbar
        setSnackbar({
          open: true,
          message: `PayPal withdrawal failed: ${error.message}`,
          severity: 'error'
        });
      }
    };

  // Process PayPal withdrawal after verification
  const processPayPalWithdrawal = async (amount, paymentMethodId, verifiedPayPalEmail) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch('https://propertyearn.net/api/api/withdrawals/request', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amount,
          payment_method_id: paymentMethodId,
          paypal_email: verifiedPayPalEmail,
          verification_type: 'paypal_external'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'PayPal withdrawal failed');
      }

      setSnackbar({
        open: true,
        message: data.message || 'PayPal withdrawal processed successfully!',
        severity: 'success'
      });

      // Refresh data and close dialogs
      await fetchEarningsData();
      setWithdrawDialogOpen(false);
      setWithdrawAmount('');
      
    } catch (error) {
      throw error;
    }
  };

  const getPaymentMethodDisplay = (method) => {
    if (!method) return { type: 'Unknown', details: '' };
    
    switch (method.type) {
      case 'credit_card':
        const last4Digits = method.card_number 
          ? method.card_number.slice(-4) 
          : method.last4 || '0000';
        
        return {
          type: 'Credit Card',
          details: `•••• ${last4Digits}`,
          extra: method.exp_month && method.exp_year ? `Exp: ${method.exp_month}/${method.exp_year}` : '',
          brand: method.brand || 'Card'
        };
      case 'paypal':
        return {
          type: 'PayPal',
          details: method.paypal_email || method.email || 'No email provided',
          extra: ''
        };
      case 'cashapp':
        return {
          type: 'CashApp',
          details: method.cashapp_cashtag || method.cashtag || 'No cashtag provided',
          extra: ''
        };
      default:
        return {
          type: method.type || 'Unknown',
          details: '',
          extra: ''
        };
    }
  };

  // Auto-select a payment method when payment methods load or change
  useEffect(() => {
    if (paymentMethods.length > 0 && !selectedPaymentMethod) {
      const methodToSelect = defaultPaymentMethod || paymentMethods[0].id;
      setSelectedPaymentMethod(methodToSelect);
    }
  }, [paymentMethods, defaultPaymentMethod, selectedPaymentMethod]);

  const handleFileUpload = (documentType, file) => {
    if (file) {
      setVerificationDocuments(prev => ({
        ...prev,
        [documentType]: file
      }));
      
      if (documentType === 'idFront') setVerificationStep(1);
      else if (documentType === 'idBack') setVerificationStep(2);
    }
  };

  const handleSubmitVerification = async () => {
    setUploading(true);
    setVerificationError('');

    try {
      const formData = new FormData();
      formData.append('id_front', verificationDocuments.idFront);
      formData.append('id_back', verificationDocuments.idBack);
      formData.append('selfie', verificationDocuments.selfie);

      const token = localStorage.getItem('token');
      const response = await fetch('https://propertyearn.net/api/api/user/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification submission failed');
      }

      setSnackbar({
        open: true,
        message: 'Verification submitted successfully! It will be reviewed within 24 hours.',
        severity: 'success'
      });

      setVerificationDialogOpen(false);
      setVerificationStep(0);
      setVerificationDocuments({
        idFront: null,
        idBack: null,
        selfie: null
      });

      await checkVerificationStatus();

    } catch (error) {
      setVerificationError(error.message);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    checkVerificationStatus();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchTransactions(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchTransactions(1, newRowsPerPage);
  };

  const handleAddPaymentMethod = async () => {
    setAddingPayment(true);
    try {
      const token = localStorage.getItem('token');

      const paymentMethodData = {
        type: newPaymentMethod.type,
        is_default: paymentMethods.length === 0 || newPaymentMethod.isDefault,
      };

      switch (newPaymentMethod.type) {
        case 'credit_card':
          paymentMethodData.card_number = newPaymentMethod.cardNumber.replace(/\s/g, '');
          paymentMethodData.cardholder_name = newPaymentMethod.cardholderName;
          paymentMethodData.expiry_month = parseInt(newPaymentMethod.expiryMonth);
          paymentMethodData.expiry_year = parseInt(newPaymentMethod.expiryYear);
          paymentMethodData.cvv = newPaymentMethod.cvv;
          paymentMethodData.brand = getCardBrand(newPaymentMethod.cardNumber.replace(/\s/g, ''));
          paymentMethodData.billing_name = billingInfo.name;
          paymentMethodData.billing_email = billingInfo.email;
          paymentMethodData.billing_phone = billingInfo.phone;
          paymentMethodData.billing_address_line1 = billingInfo.address_line1;
          paymentMethodData.billing_address_line2 = billingInfo.address_line2;
          paymentMethodData.billing_city = billingInfo.city;
          paymentMethodData.billing_state = billingInfo.state;
          paymentMethodData.billing_postal_code = billingInfo.postal_code;
          paymentMethodData.billing_country = billingInfo.country;
          break;
          
        case 'paypal':
          paymentMethodData.paypal_email = newPaymentMethod.paypalEmail;
          break;
          
        case 'cashapp':
          paymentMethodData.cashapp_cashtag = newPaymentMethod.cashappCashtag;
          break;
      }

      const response = await fetch('https://propertyearn.net/api/api/user/payment-methods-detailed', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ payment_method: paymentMethodData })
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Failed to add payment method');

      setSnackbar({
        open: true,
        message: data.message || 'Payment method added successfully!',
        severity: 'success'
      });

      await fetchPaymentMethods();
      setAddPaymentDialogOpen(false);
      resetPaymentMethodForm();
      resetBillingInfo();
      
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setAddingPayment(false);
    }
  };

  const resetPaymentMethodForm = () => {
    setNewPaymentMethod({
      type: 'credit_card',
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      cardholderName: '',
      paypalEmail: '',
      cashappCashtag: '',
      isDefault: false
    });
    resetBillingInfo();
  };

  const updatePaymentMethod = async (methodId, updates) => {
    try {
      const token = localStorage.getItem('token');
      
      if (updates.is_default) {
        await updateDefaultPaymentMethod(methodId);
      }
      
      setSnackbar({
        open: true,
        message: 'Payment method updated successfully!',
        severity: 'success'
      });
      
      setEditPaymentDialogOpen(false);
      await fetchPaymentMethods();
      
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    }
  };

const deletePaymentMethod = async (methodId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `https://propertyearn.net/api/api/user/payment-methods/${methodId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    const data = await response.json();
    
    if (!response.ok) throw new Error(data.error || 'Failed to delete payment method');

    // Use the message from backend response
    setSnackbar({
      open: true,
      message: data.message || 'Payment method deleted successfully!',
      severity: 'success'
    });

    // Refresh all data to reflect the changes
    await Promise.all([
      fetchPaymentMethods(),
      fetchEarningsData(),
      checkVerificationStatus() // Refresh verification status
    ]);
    
    // Only close the edit dialog
    setEditPaymentDialogOpen(false);
    
  } catch (error) {
    setSnackbar({
      open: true,
      message: error.message,
      severity: 'error'
    });
  }
};


// Update the export function with loading state
const handleExportTransactions = async () => {
  setExporting(true);
  try {
    const token = localStorage.getItem('token');
    
    setSnackbar({
      open: true,
      message: 'Preparing your export...',
      severity: 'info'
    });

    const response = await fetch('https://propertyearn.net/api/api/earnings/export', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to export transactions');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    
    const date = new Date().toISOString().split('T')[0];
    a.download = `propertyearn-transactions-${date}.csv`;
    
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    setSnackbar({
      open: true,
      message: 'Transactions exported successfully!',
      severity: 'success'
    });

  } catch (error) {
    console.error('Export error:', error);
    setSnackbar({
      open: true,
      message: error.message || 'Failed to export transactions',
      severity: 'error'
    });
  } finally {
    setExporting(false);
  }
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Earnings & Payments
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your earnings, view transaction history, and withdraw funds
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {verificationStatus.status !== 'approved' && verificationStatus.status !== 'checking' && (
        <Alert 
          severity={verificationStatus.status === 'pending' ? 'info' : 'warning'}
          sx={{ mb: 3, width: '100%' }}
          action={
            verificationStatus.status === 'pending' ? (
              <Button 
                color="inherit" 
                size="small"
                disabled
              >
                Under Review
              </Button>
            ) : (
              <Button 
                color="inherit" 
                size="small"
                onClick={() => setVerificationDialogOpen(true)}
              >
                Verify Now
              </Button>
            )
          }
        >
          <Typography variant="body2">
            {verificationStatus.status === 'pending' 
              ? 'Your verification is under review. You cannot withdraw funds until approved.'
              : 'Account verification required to withdraw funds.'
            }
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalanceWallet color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Available Balance</Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                £{(earningsData?.available_balance || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Available for withdrawal
              </Typography>
              <Button 
                variant="contained" 
                fullWidth
                onClick={() => setWithdrawDialogOpen(true)}
                disabled={earningsData?.available_balance < 10 || verificationStatus.status !== 'approved'}
                title={
                  verificationStatus.status !== 'approved' 
                    ? verificationStatus.status === 'pending'
                      ? "Verification under review" 
                      : "Verification required for withdrawals"
                    : "Withdraw funds"
                }
              >
                {verificationStatus.status !== 'approved' 
                  ? verificationStatus.status === 'pending'
                    ? "Verification Pending" 
                    : "Verification Required"
                  : "Withdraw Funds"
                }
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Pending color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Pending Escrow</Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                £{(earningsData?.pending_escrow || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Awaiting task approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Earned</Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                £{(earningsData?.lifetime_earnings || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Since joining PropertyEarn
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Schedule color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">This Month</Typography>
              </Box>
              <Typography variant="h4" component="div" sx={{ fontWeight: 700, mb: 1 }}>
                £{(earningsData?.monthly_earnings || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current month earnings
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab icon={<TrendingUp />} label="Earnings Trend" />
          <Tab icon={<History />} label="Transaction History" />
          <Tab icon={<Payment />} label="Payout Methods" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ height: 400, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Earnings Overview (Last 6 Months)
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={earningsData?.earnings_trend || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <RechartsTooltip formatter={(value) => [`£${value}`, "Earnings"]} />
                <Bar dataKey="earnings" fill="#4CAF50" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Transaction History</Typography>
            <Button 
              startIcon={exporting ? <CircularProgress size={16} /> : <Download />} 
              variant="outlined"
              onClick={handleExportTransactions}
              disabled={exporting || transactions.length === 0}
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Task ID</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      {transaction.date ? new Date(transaction.date).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>{transaction.description}</TableCell>
                    <TableCell>
                      {transaction.task_id ? (
                        <Button 
                          size="small" 
                          onClick={() => navigate(`/task/${transaction.task_id}`)}
                        >
                          #{transaction.task_id.substring(0, 8)}...
                        </Button>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.type.toUpperCase()}
                        color={transaction.type === 'task_earning' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontWeight: 600, 
                        color: transaction.type === 'task_earning' ? 'success.main' : 'error.main' 
                      }}
                    >
                      {transaction.type === 'task_earning' ? '+' : ''}£
                      {Math.abs(transaction.amount).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={transaction.status === 'completed' ? <CheckCircle /> : <Pending />}
                        label={transaction.status}
                        color={transaction.status === 'completed' ? 'success' : 'warning'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Payout Methods
          </Typography>
          
          {paymentMethods.length > 0 ? (
            <Box>
              {paymentMethods.map((method) => {
                const methodInfo = getPaymentMethodDisplay(method);
                return (
                  <Paper key={method.id} sx={{ p: 2, mb: 2, position: 'relative' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                        <Payment sx={{ mr: 2 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" fontWeight="medium">
                            {methodInfo.type} {methodInfo.brand && `(${methodInfo.brand})`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {methodInfo.details}
                          </Typography>
                          {methodInfo.extra && (
                            <Typography variant="caption" color="text.secondary">
                              {methodInfo.extra}
                            </Typography>
                          )}
                          {method.type === 'credit_card' && method.cardholder_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {method.cardholder_name}
                            </Typography>
                          )}
                          {method.type === 'paypal' && method.paypal_email && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {method.paypal_email}
                            </Typography>
                          )}
                          {method.type === 'cashapp' && method.cashapp_cashtag && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {method.cashapp_cashtag}
                            </Typography>
                          )}
                          {method.type === 'paypal' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <Chip 
                                label="PayPal Account" 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                                sx={{ mr: 1 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Only one PayPal account allowed per user
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                        
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {method.id === defaultPaymentMethod && (
                          <Chip label="Default" color="primary" size="small" />
                        )}
                        
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setEditingPaymentMethod(method);
                            setEditPaymentDialogOpen(true);
                          }}
                          title="Edit payment method"
                        >
                          <Edit />
                        </IconButton>
                        
                        {method.id !== defaultPaymentMethod && (
                          <Button 
                            size="small" 
                            onClick={() => updateDefaultPaymentMethod(method.id)}
                            variant="outlined"
                          >
                            Set Default
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Alert severity="info">
              No payment methods added yet. Add a payment method to withdraw your earnings.
            </Alert>
          )}

          <Button 
            startIcon={<Add />} 
            variant="contained" 
            sx={{ mt: 2 }}
            onClick={() => setAddPaymentDialogOpen(true)}
          >
            Add Payment Method
          </Button>

          <Alert severity="info" sx={{ mt: 3 }}>
            Withdrawals are typically processed within 3-5 business days. Minimum withdrawal amount is £10.00.
          </Alert>
        </TabPanel>
      </Paper>

      <Dialog open={withdrawDialogOpen} onClose={() => setWithdrawDialogOpen(false)}>
        <DialogTitle>Withdraw Funds</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Amount"
            type="number"
            fullWidth
            variant="outlined"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            inputProps={{ 
              min: 10,
              max: earningsData?.available_balance,
              step: 0.01
            }}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Payment Method</InputLabel>
            <Select
              value={selectedPaymentMethod}
              label="Payment Method"
              onChange={(e) => setSelectedPaymentMethod(e.target.value)}
            >
              {paymentMethods.map((method) => {
                const methodInfo = getPaymentMethodDisplay(method);
                return (
                  <MenuItem key={method.id} value={method.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Payment sx={{ mr: 2 }} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body1">
                          {methodInfo.type} {methodInfo.brand && `(${methodInfo.brand})`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {methodInfo.details}
                        </Typography>
                        {methodInfo.extra && (
                          <Typography variant="caption" color="text.secondary">
                            {methodInfo.extra}
                          </Typography>
                        )}
                        {method.type === 'credit_card' && method.cardholder_name && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {method.cardholder_name}
                          </Typography>
                        )}
                      </Box>
                      {method.id === defaultPaymentMethod && (
                        <Chip label="Default" color="primary" size="small" />
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
              
          {paymentMethods.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              No payment methods added. Please add a payment method first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleWithdrawal}
            disabled={
              !withdrawAmount || 
              !selectedPaymentMethod || 
              parseFloat(withdrawAmount) < 10 || 
              parseFloat(withdrawAmount) > (earningsData?.available_balance || 0) ||
              paymentMethods.length === 0
            }
            variant="contained"
          >
            Withdraw
          </Button>
        </DialogActions>

          {selectedPaymentMethod && (() => {
            const method = paymentMethods.find(m => m.id === selectedPaymentMethod);
            return method?.type === 'paypal' ? (
              <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                <Typography variant="body2">
                  <strong>PayPal Withdrawal:</strong> {
                    method.paypal_verified 
                      ? "Your PayPal account is already verified. Withdrawal will process immediately."
                      : "You will be redirected to PayPal for account verification. This ensures secure transfer of funds."
                  }
                </Typography>
              </Alert>
            ) : null;
          })()}
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog 
        open={verificationDialogOpen} 
        onClose={() => setVerificationDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VerifiedUser sx={{ mr: 1 }} />
            Identity Verification Required
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            To withdraw funds, you need to verify your identity. This helps us prevent fraud and keep your account secure.
          </Typography>

          <Stepper activeStep={verificationStep} orientation="vertical">
            <Step>
              <StepLabel>Government ID (Front)</StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Upload a clear photo of the front side of your government-issued ID
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                >
                  Upload Front of ID
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleFileUpload('idFront', e.target.files[0])}
                  />
                </Button>
                {verificationDocuments.idFront && (
                  <Typography variant="caption" sx={{ ml: 2, color: 'success.main' }}>
                    ✓ Uploaded
                  </Typography>
                )}
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Government ID (Back)</StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Upload a clear photo of the back side of your government-issued ID
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Upload />}
                >
                  Upload Back of ID
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleFileUpload('idBack', e.target.files[0])}
                  />
                </Button>
                {verificationDocuments.idBack && (
                  <Typography variant="caption" sx={{ ml: 2, color: 'success.main' }}>
                    ✓ Uploaded
                  </Typography>
                )}
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Selfie Verification</StepLabel>
              <StepContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Take a selfie holding your ID next to your face
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<Camera />}
                >
                  Take Selfie
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    capture="user"
                    onChange={(e) => handleFileUpload('selfie', e.target.files[0])}
                  />
                </Button>
                {verificationDocuments.selfie && (
                  <Typography variant="caption" sx={{ ml: 2, color: 'success.main' }}>
                    ✓ Uploaded
                  </Typography>
                )}
              </StepContent>
            </Step>
          </Stepper>

          {verificationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {verificationError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerificationDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitVerification}
            disabled={uploading || !verificationDocuments.idFront || !verificationDocuments.idBack || !verificationDocuments.selfie}
            startIcon={uploading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            {uploading ? 'Submitting...' : 'Submit Verification'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddPaymentMethodDialog
        open={addPaymentDialogOpen}
        onClose={() => {
          setAddPaymentDialogOpen(false);
          resetBillingInfo();
        }}
        onAdd={handleAddPaymentMethod}
        loading={addingPayment}
        method={newPaymentMethod}
        onChange={setNewPaymentMethod}
        isValidPaymentMethod={isValidPaymentMethod}
        billingInfo={billingInfo}
        onBillingInfoChange={handleBillingInfoChange}
        paymentMethods={paymentMethods}
      />

      <EditPaymentMethodDialog
        open={editPaymentDialogOpen}
        onClose={() => {
          setEditPaymentDialogOpen(false);
          setEditingPaymentMethod(null);
        }}
        method={editingPaymentMethod}
        onUpdate={updatePaymentMethod}
        onDelete={deletePaymentMethod}
        loading={addingPayment}
        paymentMethods={paymentMethods}
        defaultPaymentMethod={defaultPaymentMethod}
      />
    </DashboardLayout>
  );
}